import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import {
  CircleMarker,
  MapContainer,
  Marker,
  Polyline,
  Popup,
  TileLayer,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./SimulationPage.css";
import mintLogo from "./assets/mint_logo.png";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";
const MAP_CENTER = [8.7868, 78.1195];

const FALLBACK_NODES = [
  { id: "ettayapuram-road", name: "Ettayapuram Road", lat: 8.81277, lng: 78.13647 },
  { id: "fourth-gate", name: "4th Gate", lat: 8.80967, lng: 78.1335 },
  { id: "furniture-park", name: "International Furniture Park", lat: 8.81624, lng: 78.08118 },
  { id: "third-mile", name: "3rd Mile", lat: 8.789608, lng: 78.11493 },
  { id: "alageshapuram-road", name: "Alageshapuram Road", lat: 8.81705, lng: 78.14797 },
  { id: "tiruchendur-road", name: "Tiruchendur Road", lat: 8.74541, lng: 78.1253 },
];

const INTERVENTION_ICONS = {
  bus_reroute: "↗",
  signal_priority: "◉",
  truck_diversion: "⇄",
  clear_lane: "✓",
};

function formatNumber(value, digits = 0) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString("en-IN", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

function statusLabel(status) {
  return {
    idle: "Ready",
    running: "Running",
    paused: "Paused",
    completed: "Completed",
  }[status] || status || "Connecting";
}

function segmentColor(congestion) {
  const value = Number(congestion || 0);
  if (value >= 0.82) return "#ef4444";
  if (value >= 0.62) return "#f97316";
  if (value >= 0.44) return "#facc15";
  return "#34d399";
}

function aqiColor(aqi) {
  const value = Number(aqi || 0);
  if (value <= 50) return "#22c55e";
  if (value <= 100) return "#eab308";
  if (value <= 150) return "#f97316";
  if (value <= 200) return "#ef4444";
  if (value <= 300) return "#a855f7";
  return "#7f1d1d";
}

function makeDivIcon(className, html, size = [44, 44], anchor = [22, 22]) {
  return L.divIcon({
    className: "sim-leaflet-div-icon",
    html: `<div class="${className}">${html}</div>`,
    iconSize: size,
    iconAnchor: anchor,
    popupAnchor: [0, -20],
  });
}

function MiniTrendChart({ data, field, label, suffix = "", emptyText = "Start the simulation" }) {
  const points = useMemo(() => {
    if (!data?.length) return "";
    const values = data.map((item) => Number(item[field] || 0));
    const min = Math.min(...values);
    const max = Math.max(...values);
    const spread = Math.max(1, max - min);
    return values
      .map((value, index) => {
        const x = data.length === 1 ? 0 : (index / (data.length - 1)) * 100;
        const y = 38 - ((value - min) / spread) * 32;
        return `${x},${y}`;
      })
      .join(" ");
  }, [data, field]);

  const latest = data?.length ? data[data.length - 1][field] : null;

  return (
    <div className="sim-mini-chart">
      <div className="sim-mini-chart-head">
        <span>{label}</span>
        <strong>{latest === null ? "—" : `${formatNumber(latest, field.includes("Delay") ? 1 : 0)}${suffix}`}</strong>
      </div>
      {points ? (
        <svg viewBox="0 0 100 42" preserveAspectRatio="none" aria-label={`${label} trend`}>
          <defs>
            <linearGradient id={`fill-${field}`} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.34" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polygon points={`0,42 ${points} 100,42`} fill={`url(#fill-${field})`} />
          <polyline points={points} fill="none" stroke="currentColor" strokeWidth="2.2" vectorEffect="non-scaling-stroke" />
        </svg>
      ) : (
        <div className="sim-chart-empty">{emptyText}</div>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, unit, caption, tone = "default" }) {
  return (
    <article className={`sim-kpi-card tone-${tone}`}>
      <div className="sim-kpi-icon">{icon}</div>
      <div>
        <span>{label}</span>
        <strong>
          {value}
          {unit && <small>{unit}</small>}
        </strong>
        <p>{caption}</p>
      </div>
    </article>
  );
}

function LayerToggle({ checked, onChange, label, dotClass }) {
  return (
    <label className="sim-layer-toggle">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className={dotClass} />
      {label}
    </label>
  );
}

function ComparisonRow({ label, noAction, action, unit = "", lowerIsBetter = true }) {
  const max = Math.max(1, Number(noAction || 0), Number(action || 0));
  const difference = lowerIsBetter ? Number(noAction || 0) - Number(action || 0) : Number(action || 0) - Number(noAction || 0);
  const improved = difference > 0.05;

  return (
    <div className="sim-comparison-row">
      <div className="sim-comparison-label">
        <span>{label}</span>
        <em className={improved ? "is-positive" : ""}>{improved ? `Improved by ${formatNumber(difference, 1)}${unit}` : "Awaiting measurable change"}</em>
      </div>
      <div className="sim-comparison-bars">
        <div className="sim-bar-line">
          <span>No intervention</span>
          <div className="sim-bar-track"><i className="no-action" style={{ width: `${Math.max(4, (Number(noAction || 0) / max) * 100)}%` }} /></div>
          <b>{formatNumber(noAction, 1)}{unit}</b>
        </div>
        <div className="sim-bar-line">
          <span>Current strategy</span>
          <div className="sim-bar-track"><i className="with-action" style={{ width: `${Math.max(4, (Number(action || 0) / max) * 100)}%` }} /></div>
          <b>{formatNumber(action, 1)}{unit}</b>
        </div>
      </div>
    </div>
  );
}

export default function SimulationPage({ navigate }) {
  const [simulation, setSimulation] = useState(null);
  const [socketStatus, setSocketStatus] = useState("connecting");
  const [loading, setLoading] = useState(true);
  const [actionPending, setActionPending] = useState("");
  const [error, setError] = useState("");
  const [severity, setSeverity] = useState(65);
  const [duration, setDuration] = useState(18);
  const [speed, setSpeed] = useState(5);
  const [busCount, setBusCount] = useState(3);
  const [layers, setLayers] = useState({
    traffic: true,
    cameras: true,
    buses: true,
    air: true,
  });

  const applyState = useCallback((nextState) => {
    if (!nextState) return;
    setSimulation(nextState);
    if (nextState.controls) {
      setSeverity(Math.round(Number(nextState.controls.incidentSeverity || 0.65) * 100));
      setDuration(Number(nextState.controls.incidentDurationMinutes || 18));
      setBusCount(Number(nextState.controls.virtualBusCount || 3));
    }
    if (nextState.speedMultiplier) setSpeed(Number(nextState.speedMultiplier));
  }, []);

  useEffect(() => {
    let mounted = true;

    axios
      .get(`${BACKEND}/api/simulation/state`)
      .then((response) => {
        if (mounted) applyState(response.data?.state);
      })
      .catch((requestError) => {
        if (mounted) setError(requestError.response?.data?.message || "Could not load the simulation backend.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    const socket = io(BACKEND, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: Infinity,
    });

    socket.on("connect", () => setSocketStatus("connected"));
    socket.on("disconnect", () => setSocketStatus("disconnected"));
    socket.on("connect_error", () => setSocketStatus("disconnected"));
    socket.on("simulation-state", applyState);

    return () => {
      mounted = false;
      socket.off("simulation-state", applyState);
      socket.disconnect();
    };
  }, [applyState]);

  const runAction = useCallback(async (name, path, body = {}) => {
    setActionPending(name);
    setError("");
    try {
      const response = await axios.post(`${BACKEND}/api/simulation/${path}`, body);
      applyState(response.data?.state);
    } catch (requestError) {
      setError(requestError.response?.data?.message || `Could not ${name.toLowerCase()}.`);
    } finally {
      setActionPending("");
    }
  }, [applyState]);

  const status = simulation?.status || "idle";
  const isRunning = status === "running";
  const canIntervene = Boolean(simulation?.incident?.started && ["running", "paused"].includes(status));
  const kpis = simulation?.kpis || {};
  const nodes = simulation?.nodes?.length ? simulation.nodes : FALLBACK_NODES;
  const comparison = simulation?.comparison;
  const incidentNode = nodes.find((node) => node.id === "third-mile") || FALLBACK_NODES[3];

  const startSimulation = () =>
    runAction("Start simulation", "start", {
      incidentSeverity: severity / 100,
      incidentDurationMinutes: duration,
      speedMultiplier: speed,
      virtualBusCount: busCount,
    });

  const changeSpeed = (nextSpeed) => {
    setSpeed(nextSpeed);
    if (["running", "paused"].includes(status)) {
      runAction("Change simulation speed", "speed", { speedMultiplier: nextSpeed });
    }
  };

  const cameraRows = useMemo(
    () => [...(simulation?.cameras || [])].sort((a, b) => Number(b.total || 0) - Number(a.total || 0)),
    [simulation?.cameras]
  );

  const airRows = useMemo(
    () => [...(simulation?.airQuality || [])].sort((a, b) => Number(b.aqi || 0) - Number(a.aqi || 0)),
    [simulation?.airQuality]
  );

  return (
    <div className="simulation-page">
      <div className="sim-ambient sim-ambient-one" />
      <div className="sim-ambient sim-ambient-two" />

      <header className="sim-header">
        <div className="sim-brand-group">
          <button className="sim-icon-button" onClick={() => navigate("/")} aria-label="Back to home">←</button>
          <img src={mintLogo} alt="MInT Lab" />
          <div>
            <div className="sim-eyebrow">MInT Mobility Digital Twin</div>
            <h1>3rd Mile Incident Simulation</h1>
          </div>
        </div>

        <div className="sim-header-statuses">
          <span className={`sim-connection ${socketStatus}`}><i />{socketStatus === "connected" ? "Realtime connected" : "Realtime reconnecting"}</span>
          <span className={`sim-run-status status-${status}`}><i />{statusLabel(status)}</span>
          <div className="sim-clock"><small>Simulation clock</small><strong>{simulation?.simulatedClock || "08:00:00"}</strong></div>
        </div>
      </header>

      <div className="sim-disclaimer">
        <div>
          <strong>SIMULATION MODE</strong>
          <span>
            {simulation?.calibration?.active
              ? `Traffic demand calibrated from ${simulation.calibration.rowCount} recent camera rows; virtual buses and modeled air quality remain separate.`
              : "Virtual buses and modeled air-quality values. Live operational feeds remain separate."}
          </span>
        </div>
        <div className="sim-progress-wrap">
          <span>{formatNumber(simulation?.progressPct || 0, 0)}%</span>
          <div><i style={{ width: `${simulation?.progressPct || 0}%` }} /></div>
        </div>
      </div>

      {error && <div className="sim-error-banner"><span>⚠</span>{error}<button onClick={() => setError("")}>×</button></div>}

      <main className="sim-shell">
        <aside className="sim-control-panel">
          <div className="sim-panel-title">
            <div><span>Scenario controls</span><h2>Configure the disruption</h2></div>
            <span className="sim-scenario-number">01</span>
          </div>

          <div className={`sim-calibration-badge ${simulation?.calibration?.active ? "active" : ""}`}>
            <i />
            <div>
              <strong>{simulation?.calibration?.active ? "Camera-calibrated demand" : "Modeled baseline demand"}</strong>
              <small>
                {simulation?.calibration?.active
                  ? `${simulation.calibration.averageVehiclesPer5Sec} vehicles / 5 sec average`
                  : "Recent camera rows will be used automatically when available"}
              </small>
            </div>
          </div>

          <div className="sim-control-section">
            <label className="sim-range-label">
              <span><b>Incident severity</b><em>{severity}% capacity disruption</em></span>
              <strong>{severity}%</strong>
            </label>
            <input type="range" min="20" max="90" step="5" value={severity} disabled={isRunning} onChange={(event) => setSeverity(Number(event.target.value))} />
            <div className="sim-range-scale"><span>Minor</span><span>Severe</span></div>
          </div>

          <div className="sim-control-two-col">
            <label>
              <span>Incident duration</span>
              <select value={duration} disabled={isRunning} onChange={(event) => setDuration(Number(event.target.value))}>
                {[10, 14, 18, 22, 26, 30].map((value) => <option key={value} value={value}>{value} min</option>)}
              </select>
            </label>
            <label>
              <span>Virtual buses</span>
              <select value={busCount} disabled={isRunning} onChange={(event) => setBusCount(Number(event.target.value))}>
                {[2, 3, 4, 5].map((value) => <option key={value} value={value}>{value} buses</option>)}
              </select>
            </label>
          </div>

          <div className="sim-control-section">
            <div className="sim-section-label"><span>Playback speed</span><em>5 simulated seconds per step</em></div>
            <div className="sim-speed-buttons">
              {[1, 2, 5, 10].map((value) => (
                <button key={value} className={speed === value ? "active" : ""} onClick={() => changeSpeed(value)}>{value}×</button>
              ))}
            </div>
          </div>

          <div className="sim-primary-actions">
            {status === "idle" || status === "completed" ? (
              <button className="sim-start-button" disabled={Boolean(actionPending)} onClick={startSimulation}>
                <span>▶</span>{status === "completed" ? "Run again" : "Start simulation"}
              </button>
            ) : isRunning ? (
              <button className="sim-pause-button" disabled={Boolean(actionPending)} onClick={() => runAction("Pause simulation", "pause")}><span>Ⅱ</span>Pause</button>
            ) : (
              <button className="sim-start-button" disabled={Boolean(actionPending)} onClick={() => runAction("Resume simulation", "resume")}><span>▶</span>Resume</button>
            )}
            <button className="sim-reset-button" disabled={Boolean(actionPending)} onClick={() => runAction("Reset simulation", "reset")}>↻ Reset</button>
          </div>

          <div className="sim-intervention-header">
            <div><span>Response centre</span><h3>Apply an intervention</h3></div>
            <i className={canIntervene ? "ready" : ""}>{canIntervene ? "LIVE" : "LOCKED"}</i>
          </div>

          <div className="sim-intervention-list">
            {(simulation?.availableInterventions || []).map((item) => {
              const active = simulation?.intervention?.type === item.id;
              return (
                <button key={item.id} className={active ? "active" : ""} disabled={!canIntervene || Boolean(actionPending)} onClick={() => runAction(item.name, "intervention", { type: item.id })}>
                  <span>{INTERVENTION_ICONS[item.id] || "+"}</span>
                  <div><strong>{item.name}</strong><small>{item.description}</small></div>
                  <b>{active ? "ACTIVE" : "APPLY"}</b>
                </button>
              );
            })}
          </div>

          {!canIntervene && <p className="sim-intervention-hint">Interventions unlock when the incident begins at simulation minute 03:00.</p>}
        </aside>

        <section className="sim-main-content">
          <section className="sim-map-card">
            <div className="sim-map-toolbar">
              <div>
                <span>Unified operations map</span>
                <h2>Thoothukudi mobility network</h2>
              </div>
              <div className="sim-layer-controls">
                <LayerToggle label="Traffic" checked={layers.traffic} dotClass="dot-traffic" onChange={() => setLayers((current) => ({ ...current, traffic: !current.traffic }))} />
                <LayerToggle label="Cameras" checked={layers.cameras} dotClass="dot-camera" onChange={() => setLayers((current) => ({ ...current, cameras: !current.cameras }))} />
                <LayerToggle label="Buses" checked={layers.buses} dotClass="dot-bus" onChange={() => setLayers((current) => ({ ...current, buses: !current.buses }))} />
                <LayerToggle label="Air quality" checked={layers.air} dotClass="dot-air" onChange={() => setLayers((current) => ({ ...current, air: !current.air }))} />
              </div>
            </div>

            <div className="sim-map-wrap">
              <MapContainer center={MAP_CENTER} zoom={12.8} minZoom={11} scrollWheelZoom className="sim-map">
                <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                {layers.traffic && (simulation?.segments || []).map((segment) => (
                  <Polyline key={segment.id} positions={(segment.points || []).map((point) => [point.lat, point.lng])} pathOptions={{ color: segmentColor(segment.congestion), weight: segment.incidentAffected ? 8 : 6, opacity: 0.88, dashArray: segment.detour ? "7 8" : undefined }}>
                    <Popup>
                      <div className="sim-map-popup">
                        <strong>{segment.name}</strong>
                        <span>Speed: {formatNumber(segment.currentSpeedKmph, 1)} km/h</span>
                        <span>Congestion: {formatNumber(Number(segment.congestion || 0) * 100)}%</span>
                        <span>Queue: {formatNumber(segment.queueVehicles, 0)} vehicles</span>
                        <span>Capacity: {formatNumber(segment.effectiveCapacityVehPerMin, 0)} veh/min</span>
                      </div>
                    </Popup>
                  </Polyline>
                ))}

                {layers.buses && (simulation?.buses || []).filter((bus) => bus.rerouted).map((bus) => (
                  <Polyline key={`route-${bus.id}`} positions={(bus.routePoints || []).map((point) => [point.lat, point.lng])} pathOptions={{ color: "#22d3ee", weight: 3, opacity: 0.75, dashArray: "5 8" }} />
                ))}

                {layers.air && (simulation?.airQuality || []).map((sensor) => (
                  <CircleMarker key={sensor.sensorId} center={[sensor.lat, sensor.lng]} radius={8 + Math.min(12, Number(sensor.aqi || 0) / 18)} pathOptions={{ color: aqiColor(sensor.aqi), fillColor: aqiColor(sensor.aqi), fillOpacity: 0.24, weight: 2 }}>
                    <Popup><div className="sim-map-popup"><strong>{sensor.name} · Modeled AQ</strong><span>AQI: {sensor.aqi} ({sensor.category})</span><span>PM2.5: {sensor.pm25} µg/m³</span><span>NO₂: {sensor.no2} µg/m³</span></div></Popup>
                  </CircleMarker>
                ))}

                {layers.cameras && (simulation?.cameras || []).map((camera) => (
                  <Marker key={camera.cameraId} position={[camera.lat, camera.lng]} icon={makeDivIcon("sim-camera-marker", `<span>${camera.total}</span><small>CAM</small>`, [48, 48], [24, 24])}>
                    <Popup><div className="sim-map-popup"><strong>{camera.cameraName}</strong><span>{camera.total} vehicles / 5 sec</span><span>Cars {camera.car} · Bikes {camera.motorbike}</span><span>Bus {camera.bus} · Truck {camera.truck} · Auto {camera.auto}</span></div></Popup>
                  </Marker>
                ))}

                {layers.buses && (simulation?.buses || []).map((bus) => (
                  <Marker key={bus.id} position={[bus.lat, bus.lng]} icon={makeDivIcon(`sim-bus-marker${bus.rerouted ? " rerouted" : ""}`, `<span>🚌</span><small>${bus.id.replace("SIM-BUS-", "")}</small>`, [50, 50], [25, 25])}>
                    <Popup><div className="sim-map-popup"><strong>{bus.id}</strong><span>{bus.routeName}</span><span>Speed: {bus.speedKmph} km/h</span><span>Delay: {bus.delayMinutes} min</span><span>Next: {bus.nextStop}</span><span>{bus.rerouted ? "Running on diversion" : "Running on normal corridor"}</span></div></Popup>
                  </Marker>
                ))}

                {simulation?.incident?.started && (
                  <Marker position={[incidentNode.lat, incidentNode.lng]} icon={makeDivIcon(`sim-incident-marker ${simulation.incident.active ? "active" : "recovering"}`, `<span>!</span>`, [54, 54], [27, 27])}>
                    <Popup><div className="sim-map-popup"><strong>3rd Mile incident</strong><span>{simulation.incident.type}</span><span>Status: {simulation.incident.status}</span><span>Effective severity: {formatNumber(Number(simulation.incident.effectiveSeverity || 0) * 100)}%</span></div></Popup>
                  </Marker>
                )}
              </MapContainer>

              <div className={`sim-incident-overlay state-${simulation?.incident?.status || "scheduled"}`}>
                <div className="sim-incident-symbol">!</div>
                <div><span>3rd Mile event</span><strong>{simulation?.incident?.status === "scheduled" ? "Incident scheduled at 08:03" : simulation?.incident?.active ? "Lane blockage active" : "Corridor recovering"}</strong></div>
                <b>{formatNumber(Number(simulation?.incident?.effectiveSeverity || 0) * 100)}%</b>
              </div>

              <div className="sim-map-legend">
                <span><i style={{ background: "#34d399" }} />Free flow</span>
                <span><i style={{ background: "#facc15" }} />Busy</span>
                <span><i style={{ background: "#f97316" }} />Congested</span>
                <span><i style={{ background: "#ef4444" }} />Severe</span>
              </div>
            </div>
          </section>

          <section className="sim-kpi-grid">
            <KpiCard icon="⌁" label="Average traffic speed" value={formatNumber(kpis.averageTrafficSpeedKmph, 1)} unit="km/h" caption={`${formatNumber(kpis.averageCongestionPct)}% network congestion`} tone={Number(kpis.averageTrafficSpeedKmph) < 18 ? "danger" : "good"} />
            <KpiCard icon="≋" label="Maximum queue" value={formatNumber(kpis.maximumQueueVehicles)} unit="vehicles" caption="Worst affected road segment" tone={Number(kpis.maximumQueueVehicles) > 45 ? "danger" : "warning"} />
            <KpiCard icon="🚌" label="Average bus delay" value={formatNumber(kpis.averageBusDelayMinutes, 1)} unit="min" caption={`${formatNumber(kpis.onTimeBusPct)}% buses on time`} tone={Number(kpis.averageBusDelayMinutes) > 5 ? "danger" : "good"} />
            <KpiCard icon="AQ" label="Peak modeled AQI" value={formatNumber(kpis.maximumAqi)} unit="AQI" caption="Modeled, not sensor measurement" tone={Number(kpis.maximumAqi) > 150 ? "danger" : "warning"} />
            <KpiCard icon="◫" label="Camera vehicle count" value={formatNumber(kpis.totalCameraVehicles)} unit="/ 5 sec" caption="Across six simulated camera sites" />
            <KpiCard icon="↘" label="Passenger time saved" value={formatNumber(kpis.passengerMinutesSaved)} unit="min" caption={simulation?.intervention ? `Using ${simulation.intervention.name}` : "Apply an intervention to compare"} tone={Number(kpis.passengerMinutesSaved) > 0 ? "good" : "default"} />
          </section>

          <section className="sim-insight-grid">
            <article className="sim-card sim-trend-card">
              <div className="sim-card-heading"><div><span>Network response</span><h2>Impact over simulation time</h2></div><em>{simulation?.history?.length || 0} samples</em></div>
              <div className="sim-trend-grid">
                <MiniTrendChart data={simulation?.history} field="congestionPct" label="Congestion" suffix="%" />
                <MiniTrendChart data={simulation?.history} field="busDelayMinutes" label="Bus delay" suffix=" min" />
                <MiniTrendChart data={simulation?.history} field="aqi" label="Peak AQI" />
              </div>
            </article>

            <article className="sim-card sim-timeline-card">
              <div className="sim-card-heading"><div><span>Operations log</span><h2>Scenario timeline</h2></div><em>Latest first</em></div>
              <div className="sim-timeline-list">
                {(simulation?.timeline || []).slice(0, 6).map((event) => (
                  <div key={event.id} className={`sim-timeline-item level-${event.level}`}>
                    <i />
                    <time>{event.clock}</time>
                    <div><strong>{event.title}</strong><p>{event.message}</p></div>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="sim-data-grid">
            <article className="sim-card">
              <div className="sim-card-heading"><div><span>Camera layer</span><h2>Latest simulated vehicle counts</h2></div><em>5-second window</em></div>
              <div className="sim-table-wrap">
                <table className="sim-data-table">
                  <thead><tr><th>Location</th><th>Total</th><th>Cars</th><th>Bikes</th><th>Heavy</th><th>Congestion</th></tr></thead>
                  <tbody>
                    {cameraRows.map((row) => (
                      <tr key={row.cameraId}><td><strong>{row.cameraName}</strong><small>{row.direction}</small></td><td>{row.total}</td><td>{row.car}</td><td>{row.motorbike}</td><td>{Number(row.bus || 0) + Number(row.truck || 0)}</td><td><span className={`sim-table-status ${Number(row.congestionPct) >= 70 ? "high" : Number(row.congestionPct) >= 45 ? "medium" : "low"}`}>{row.congestionPct}%</span></td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="sim-card">
              <div className="sim-card-heading"><div><span>Environmental layer</span><h2>Modeled air-quality hotspots</h2></div><em>Not physical sensor data</em></div>
              <div className="sim-air-list">
                {airRows.map((row, index) => (
                  <div className="sim-air-row" key={row.sensorId}>
                    <span className="sim-air-rank">{String(index + 1).padStart(2, "0")}</span>
                    <div><strong>{row.name}</strong><small>PM2.5 {row.pm25} · NO₂ {row.no2}</small></div>
                    <span className="sim-aqi-pill" style={{ "--aqi-color": aqiColor(row.aqi) }}><b>{row.aqi}</b><small>{row.category}</small></span>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="sim-card sim-comparison-card">
            <div className="sim-card-heading"><div><span>Decision intelligence</span><h2>No intervention vs current response</h2></div><em>{simulation?.intervention?.name || "No intervention selected"}</em></div>
            <div className="sim-comparison-grid">
              <ComparisonRow label="Average bus delay" noAction={comparison?.noIntervention?.averageBusDelayMinutes || 0} action={comparison?.withIntervention?.averageBusDelayMinutes || 0} unit=" min" />
              <ComparisonRow label="Maximum congestion" noAction={comparison?.noIntervention?.maximumCongestionPct || 0} action={comparison?.withIntervention?.maximumCongestionPct || 0} unit="%" />
              <ComparisonRow label="Peak modeled AQI" noAction={comparison?.noIntervention?.maximumAqi || 0} action={comparison?.withIntervention?.maximumAqi || 0} unit="" />
              <ComparisonRow label="Passenger delay" noAction={comparison?.noIntervention?.passengerDelayMinutes || 0} action={comparison?.withIntervention?.passengerDelayMinutes || 0} unit=" min" />
            </div>
            <div className="sim-savings-strip">
              <div><span>Estimated passenger time saved</span><strong>{formatNumber(comparison?.savings?.passengerMinutes || 0)} min</strong></div>
              <div><span>Bus-delay reduction</span><strong>{formatNumber(comparison?.savings?.delayMinutes || 0, 1)} min</strong></div>
              <div><span>AQI reduction</span><strong>{formatNumber(comparison?.savings?.aqiPoints || 0)} points</strong></div>
              <div><span>Projected recovery</span><strong>{formatNumber(kpis.projectedRecoveryMinutes || 0)} min</strong></div>
            </div>
          </section>
        </section>
      </main>

      {loading && <div className="sim-loading-overlay"><div className="sim-loader" /><span>Loading digital twin…</span></div>}
      {actionPending && <div className="sim-action-toast"><i />{actionPending}…</div>}
    </div>
  );
}
